import { useStore } from '../services/store';
import { CheckCircle2, Clock, Loader2, XCircle, ExternalLink, ChevronDown, ArrowRight } from 'lucide-react';

interface Props { jobId: string }

export default function ShotStatusPanel({ jobId }: Props) {
  const { shots, setActiveTab } = useStore();
  // Always show ALL shots across all jobs so the full batch is visible
  const display = shots;

  const counts = {
    pending:    display.filter(s => s.status === 'pending' || s.status === 'ready').length,
    generating: display.filter(s => s.status === 'generating' || s.status === 'uploading').length,
    done:       display.filter(s => s.status === 'done').length,
    error:      display.filter(s => s.status === 'error').length,
  };

  const statusIcon = (status: string) => {
    if (status === 'done')       return <CheckCircle2 className="w-3 h-3 text-[#76a441]" />;
    if (status === 'generating') return <Loader2 className="w-3 h-3 text-[#258cc0] animate-spin" />;
    if (status === 'uploading')  return <Loader2 className="w-3 h-3 text-[#865916] animate-spin" />;
    if (status === 'error')      return <XCircle className="w-3 h-3 text-[#dc553c]" />;
    return <Clock className="w-3 h-3 text-[#a89470]" />;
  };

  return (
    <div className="flex flex-col h-full bg-[#fdf6e9] border-l border-[#d6bd7c]/60">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-[#d6bd7c]/60 px-3 py-2.5 shrink-0">
        <span className="text-xs font-black tracking-[0.1em] uppercase text-[#2f2414]">Shot Queue</span>
        <span className="rounded-full bg-[#865916] px-1.5 py-0.5 text-[10px] font-black text-[#fff8ec]">{display.length}</span>
      </div>

      {/* Stats */}
      {display.length > 0 && (
        <div className="grid grid-cols-2 gap-1 p-2 border-b border-[#d6bd7c]/40 shrink-0">
          {[
            { label: 'Pending', val: counts.pending, color: 'text-[#6d5b3f]', bg: 'bg-[#e9d6aa]/60' },
            { label: 'Running', val: counts.generating, color: 'text-[#258cc0]', bg: 'bg-[#258cc0]/10' },
            { label: 'Done',    val: counts.done, color: 'text-[#476b24]', bg: 'bg-[#76a441]/10' },
            { label: 'Error',   val: counts.error, color: 'text-[#8c2d1f]', bg: 'bg-[#dc553c]/10' },
          ].map(({ label, val, color, bg }) => (
            <div key={label} className={`rounded-lg px-2 py-1.5 text-center ${bg}`}>
              <div className={`text-sm font-black ${color}`}>{val}</div>
              <div className="text-[9px] font-bold text-[#6d5b3f]">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Shot list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {display.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-6">
            <ChevronDown className="w-6 h-6 text-[#d6bd7c] mb-2" />
            <p className="text-[10px] text-[#a89470] font-medium leading-relaxed">
              Click Start Processing<br />to break script into shots
            </p>
          </div>
        ) : (
          display.map(shot => (
            <div key={shot.id} className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 ${
              shot.status === 'done'   ? 'border-[#76a441]/30 bg-[#76a441]/5' :
              shot.status === 'error' ? 'border-[#dc553c]/30 bg-[#dc553c]/5' :
              shot.status === 'generating' ? 'border-[#258cc0]/30 bg-[#258cc0]/5' :
              'border-[#d6bd7c]/40 bg-[#fff8ec]/60'
            }`}>
              <div className="mt-0.5 shrink-0">{statusIcon(shot.status)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-[10px] font-black text-[#865916]">S{shot.shotNumber}</span>
                  <span className="text-[9px] text-[#a89470]">{shot.durationSec}s</span>
                  {shot.boardId && (
                    <a href={`/flowboard/#board=${shot.boardId}`} target="_blank" rel="noopener noreferrer"
                      className="text-[9px] text-[#258cc0]"><ExternalLink className="w-2.5 h-2.5" /></a>
                  )}
                </div>
                <p className="text-[9px] text-[#6d5b3f] line-clamp-2 leading-relaxed">{shot.prompt}</p>
                {shot.status === 'error' && shot.errorMsg && (
                  <p className="text-[9px] text-[#dc553c] mt-0.5 line-clamp-1">{shot.errorMsg}</p>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Go to Normal Panel */}
      {display.length > 0 && (
        <div className="border-t border-[#d6bd7c]/40 p-2 shrink-0">
          <button
            onClick={() => setActiveTab('normalPanel')}
            className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-[#865916] py-2 text-xs font-bold text-white hover:bg-[#6d4712] transition-colors"
          >
            <ArrowRight className="w-3.5 h-3.5" />Open Normal Panel
          </button>
        </div>
      )}
    </div>
  );
}
